import { CorporateAdmins, Users } from '@platform/client';
import type { CohortType } from '@platform/client/schemas';
import { PillBadge, type PillBadgeColors } from '@platform/components';
import { useEffect, useMemo } from 'react';
import { useLocation, useSearch } from 'wouter';
import { navigate } from 'wouter/use-browser-location';
import { AuthedPage, HeaderFooterLayout } from '../app';
import { formatDateStr } from '../dashboard/my-projects/utils';
import { ROUTES } from '../routes/consts';
import { ApplicantsTable } from './dashboard/ApplicantsTable';
import { CohortsDropDown, sortCohortsForDisplay } from './dashboard/CohortsDropDown';
import { CohortStatsBox } from './dashboard/CohortStatsBox';

const COHORT_TYPE_PILL_COLOR: Record<CohortType, PillBadgeColors> = {
  Teams: 'blue',
  Individual: 'success-25',
  'Individual - Assigned': 'secondary-brand-50',
  'Self-Paced - Individual': 'orange',
};

const CORPORATE_CLIENT_DASHBOARD_TITLE: Record<number, string> = {
  1: 'SAP Acceleration Collective Dashboard',
  2: 'EY Pro Bono Connect Dashboard',
  3: 'Lumen Volunteer Engagement Dashboard',
  4: 'F5 Community Impact Dashboard',
};

export const CorporateDashboard = () => {
  const { data: user, isSuccess, isLoading, isError } = Users.useGetCurrentUser();
  const { data: cohortsData } = CorporateAdmins.useGetCohorts();
  const search = useSearch();
  const [, setLocation] = useLocation();
  const cohortIdParam = new URLSearchParams(search).get('cohortId');
  const selectedCohortId = cohortIdParam ? Number(cohortIdParam) : undefined;
  const setSelectedCohortId = (id: number) => {
    const next = new URLSearchParams(search);
    next.set('cohortId', String(id));
    setLocation(`${ROUTES.CORPORATE_DASHBOARD}?${next.toString()}`);
  };
  const isCorporateAdmin = user?.accountType === 'corporate';
  const sortedCohorts = useMemo(
    () => sortCohortsForDisplay(cohortsData?.cohorts ?? []),
    [cohortsData],
  );
  const selectedCohort = sortedCohorts.find((c) => c.id === selectedCohortId);

  useEffect(() => {
    if (isSuccess && !isCorporateAdmin) {
      navigate('/');
    }
  }, [isSuccess, isCorporateAdmin]);

  useEffect(() => {
    if (!isCorporateAdmin) return;
    if (cohortIdParam) return;
    if (sortedCohorts.length === 0) return;
    setSelectedCohortId(sortedCohorts[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCorporateAdmin, cohortIdParam, sortedCohorts]);

  return (
    <AuthedPage>
      <HeaderFooterLayout>
        <div className='w-full px-6 md:px-10 lg:px-16 pb-10 bg-gray-50'>
        {isError && <div>Error loading user data</div>}
        {isLoading && <div>Loading...</div>}
        {isSuccess && isCorporateAdmin && (
          <>
            <h1 className='text-4xl font-medium tracking-tight text-gray-600'>
              {(user?.corporateClientId &&
                CORPORATE_CLIENT_DASHBOARD_TITLE[user.corporateClientId]) ??
                `${user?.corporateClientName ?? 'Corporate'} Dashboard`}
            </h1>

            <div className='mt-6 flex flex-col gap-2'>
              <span className='text-base font-medium text-gray-700'>Cohort</span>
              <div className='flex items-center gap-3'>
                <CohortsDropDown
                  selectedCohortId={selectedCohortId}
                  onCohortSelect={setSelectedCohortId}
                />
                {selectedCohort && selectedCohort.type && (
                  <PillBadge
                    variant='plain'
                    size='md'
                    color={COHORT_TYPE_PILL_COLOR[selectedCohort.type]}
                  >
                    {selectedCohort.type}
                  </PillBadge>
                )}
              </div>
            </div>

            {selectedCohort && (
              <div className='mt-4 flex flex-wrap gap-x-8 gap-y-2 text-md'>
                <div>
                  <span className='font-medium text-gray-500'>Application Period: </span>
                  <span className='font-semibold text-gray-700'>
                    {selectedCohort.applicationStart ?
                      formatDateStr(selectedCohort.applicationStart)
                    : '—'}
                    {' - '}
                    {selectedCohort.applicationEnd ?
                      formatDateStr(selectedCohort.applicationEnd)
                    : '—'}
                  </span>
                </div>
                <div>
                  <span className='font-medium text-gray-500'>Cohort Dates: </span>
                  <span className='font-semibold text-gray-700'>
                    {selectedCohort.startDate ? formatDateStr(selectedCohort.startDate) : '—'}
                    {' - '}
                    {selectedCohort.endDate ? formatDateStr(selectedCohort.endDate) : '—'}
                  </span>
                </div>
              </div>
            )}

            {selectedCohortId && selectedCohort && (
              <>
                <div className='mt-8'>
                  <CohortStatsBox cohortId={selectedCohortId} />
                </div>
                <div className='mt-8'>
                  <ApplicantsTable cohortId={selectedCohortId} cohort={selectedCohort} />
                </div>
              </>
            )}
          </>
        )}
        </div>
      </HeaderFooterLayout>
    </AuthedPage>
  );
};
